import { Check, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  type AddressSelection,
  type GoogleAddressComponent,
  parseGoogleAddressComponents,
} from '../../lib/address';
import { loadGoogleMaps } from '../../lib/google-maps-loader';
import { Input } from '../../ui/input';

export type { AddressSelection } from '../../lib/address';

type AddressAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onSelectAddress: (result: AddressSelection) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  apiKey?: string;
  initialLatitude?: number | null;
  initialLongitude?: number | null;
  id?: string;
};

/** Shared Google Places field that returns a normalized address selection. */
export function AddressAutocomplete({
  value,
  onChange,
  onSelectAddress,
  placeholder = 'Search address or place...',
  required = false,
  className,
  apiKey,
  initialLatitude,
  initialLongitude,
  id,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasValidCoords, setHasValidCoords] = useState(
    initialLatitude != null && initialLongitude != null
  );
  const onChangeRef = useRef(onChange);
  const onSelectAddressRef = useRef(onSelectAddress);

  useEffect(() => {
    onChangeRef.current = onChange;
    onSelectAddressRef.current = onSelectAddress;
  }, [onChange, onSelectAddress]);

  useEffect(() => {
    setHasValidCoords(initialLatitude != null && initialLongitude != null);
  }, [initialLatitude, initialLongitude]);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps(apiKey)
      .then((googleMaps) => {
        if (!isMounted || !inputRef.current || !googleMaps.places) return;

        const autocomplete = new googleMaps.places.Autocomplete(inputRef.current, {
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name'],
        });
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place) return;

          const formattedAddress = place.formatted_address || place.name || '';
          const latitude = place.geometry?.location?.lat() ?? null;
          const longitude = place.geometry?.location?.lng() ?? null;
          const components = (place.address_components ?? []).map((component) => ({
            longName: component.long_name,
            shortName: component.short_name,
            types: component.types,
          })) satisfies GoogleAddressComponent[];

          onChangeRef.current(formattedAddress);
          setHasValidCoords(latitude != null && longitude != null);
          onSelectAddressRef.current({
            ...parseGoogleAddressComponents(components),
            formattedAddress,
            googlePlaceId: place.place_id ?? null,
            latitude,
            longitude,
          });
        });
      })
      .catch(() => {
        // Manual address entry remains available when a browser Maps key is not configured.
      });

    return () => {
      isMounted = false;
    };
  }, [apiKey]);

  return (
    <div className="space-y-1.5 w-full">
      <div className="relative flex items-center">
        <MapPin className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
        <Input
          id={id}
          ref={inputRef}
          className={`pl-9 pr-8 ${className || ''}`}
          onChange={(event) => {
            onChange(event.target.value);
            setHasValidCoords(false);
          }}
          placeholder={placeholder}
          required={required}
          type="text"
          value={value}
        />
        {hasValidCoords && (
          <div
            className="pointer-events-none absolute top-1/2 right-2.5 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600"
            title="Verified geocoded address"
          >
            <Check className="h-3 w-3 stroke-[3]" />
          </div>
        )}
      </div>
      <p className="px-0.5 text-[11px] text-muted-foreground">
        {hasValidCoords
          ? 'Google Maps verified this location.'
          : 'Select a suggested address to fill its components.'}
      </p>
    </div>
  );
}
